import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  Paper,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import SpaIcon from '@mui/icons-material/Spa';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

const SCHEMES = [
  {
    slug: 'gymify',
    label: 'NORI',
    blurb: 'The simple CRM — electric lime on deep black',
    headingFont: 'Geist',
    icon: StorefrontIcon,
    swatches: ['#CEFB52', '#0F0F10', '#1F7CFF'],
    headingStyle: { fontWeight: 700 },
  },
  {
    slug: 'barbershop',
    label: 'Barbershop',
    blurb: 'Classic ivory and oxblood',
    headingFont: 'Playfair Display',
    icon: ContentCutIcon,
    swatches: ['#7B1E1E', '#F5EFE6', '#3A2A1A'],
    headingStyle: { fontWeight: 700, fontStyle: 'italic' },
  },
  {
    slug: 'serenity',
    label: 'Spa',
    blurb: 'Soft sage and warm sand',
    headingFont: 'Cormorant Garamond',
    icon: SpaIcon,
    swatches: ['#7FA993', '#F2EAD9', '#4D6A5A'],
    headingStyle: { fontWeight: 500, letterSpacing: '0.03em' },
  },
  {
    slug: 'custom',
    label: 'Superadmin',
    blurb: 'NORI HQ — sign in as superadmin and switch into any tenant',
    headingFont: 'Geist',
    icon: AdminPanelSettingsIcon,
    swatches: ['#6366F1', '#14B8A6', '#0F0F10'],
    headingStyle: { fontWeight: 700 },
  },
];

const SchemeCard = ({ scheme, onSelect }) => {
  const theme = useTheme();
  const Icon = scheme.icon;
  return (
    <Paper
      variant="outlined"
      role="button"
      tabIndex={0}
      onClick={() => onSelect(scheme.slug)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(scheme.slug);
        }
      }}
      data-testid={`scheme-card-${scheme.slug}`}
      sx={{
        p: 2.5,
        cursor: 'pointer',
        flex: 1,
        minWidth: 0,
        transition: 'transform 120ms ease, border-color 120ms ease',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        '&:hover, &:focus-visible': {
          borderColor: 'primary.main',
          transform: 'translateY(-2px)',
          outline: 'none',
        },
      }}
    >
      <Icon sx={{ fontSize: 32, color: scheme.swatches[0], mb: 1 }} />
      <Typography
        variant="h6"
        sx={{
          fontFamily: `"${scheme.headingFont}", "Geist", sans-serif`,
          color: theme.palette.text.primary,
          mb: 0.5,
          ...scheme.headingStyle,
        }}
      >
        {scheme.label}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {scheme.blurb}
      </Typography>
      <Stack direction="row" spacing={1}>
        {scheme.swatches.map((color) => (
          <Box
            key={color}
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              bgcolor: color,
              border: 1,
              borderColor: 'divider',
            }}
          />
        ))}
      </Stack>
    </Paper>
  );
};

const SchemePickerDialog = ({ open, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const handleSelect = (slug) => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('tenant_slug', slug);
    window.location.reload();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      data-testid="scheme-picker-dialog"
    >
      <DialogTitle sx={{ pb: 0 }}>Choose a demo</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Pick which tenant brand to preview. The page will reload.
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ alignItems: 'stretch' }}
        >
          {SCHEMES.map((scheme) => (
            <SchemeCard key={scheme.slug} scheme={scheme} onSelect={handleSelect} />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SchemePickerDialog;
